import React, { useState, useEffect } from 'react';
import './MarketGauges.css';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

function fgColor(v) {
  if (v <= 25) return '#ef5350';
  if (v <= 45) return '#f97316';
  if (v <= 55) return '#facc15';
  if (v <= 75) return '#4ade80';
  return '#26a69a';
}

function fgLabel(v) {
  if (v <= 25) return 'Medo Extremo';
  if (v <= 45) return 'Medo';
  if (v <= 55) return 'Neutro';
  if (v <= 75) return 'Ganância';
  return 'Gan. Extrema';
}

function vixColor(v) {
  if (v < 15) return '#26a69a';
  if (v < 20) return '#4ade80';
  if (v < 25) return '#facc15';
  if (v < 30) return '#f97316';
  return '#ef5350';
}

function vixLabel(v) {
  if (v < 15) return 'Calmo';
  if (v < 20) return 'Normal';
  if (v < 25) return 'Elevado';
  if (v < 30) return 'Alto';
  return 'Pânico';
}

function ArcGauge({ value, max, color, label, valueLabel, motivo }) {
  const W = 64; const H = 40;
  const CX = W / 2; const CY = H - 4;
  const R = 23;
  const pct = Math.min(1, Math.max(0, (value ?? 0) / max));
  const angleDeg = -180 + pct * 180;
  const toRad = d => (d * Math.PI) / 180;

  const arcPoint = (r, deg) => ({
    x: CX + r * Math.cos(toRad(deg)),
    y: CY + r * Math.sin(toRad(deg)),
  });

  const arcD = (r, a1, a2) => {
    const p1 = arcPoint(r, a1);
    const p2 = arcPoint(r, a2);
    const large = (a2 - a1) > 180 ? 1 : 0;
    return `M ${p1.x.toFixed(2)},${p1.y.toFixed(2)} A ${r},${r} 0 ${large},1 ${p2.x.toFixed(2)},${p2.y.toFixed(2)}`;
  };

  // Needle: thin triangle from pivot to tip + short tail
  const needleLen  = R - 2;
  const tailLen    = 5;
  const halfW      = 1.6;
  const ang        = toRad(angleDeg);
  const perp       = ang + Math.PI / 2;

  const tip  = { x: CX + needleLen * Math.cos(ang),  y: CY + needleLen * Math.sin(ang) };
  const bl   = { x: CX + halfW * Math.cos(perp) - tailLen * Math.cos(ang),
                 y: CY + halfW * Math.sin(perp) - tailLen * Math.sin(ang) };
  const br   = { x: CX - halfW * Math.cos(perp) - tailLen * Math.cos(ang),
                 y: CY - halfW * Math.sin(perp) - tailLen * Math.sin(ang) };
  const needlePath = `M ${tip.x.toFixed(2)},${tip.y.toFixed(2)} L ${bl.x.toFixed(2)},${bl.y.toFixed(2)} L ${br.x.toFixed(2)},${br.y.toFixed(2)} Z`;

  return (
    <div className="mg-gauge">
      <div className="mg-label">{label}</div>
      <svg viewBox={`0 0 ${W} ${H}`} className="mg-svg">
        {/* Track */}
        <path d={arcD(R, -180, 0)}
          fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="4" strokeLinecap="round" />
        {/* Fill */}
        {value != null && (
          <path d={arcD(R, -180, angleDeg)}
            fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" opacity="0.85" />
        )}
        {/* Needle shadow for depth */}
        {value != null && (
          <polygon
            points={`${tip.x.toFixed(2)},${tip.y.toFixed(2)} ${bl.x.toFixed(2)},${bl.y.toFixed(2)} ${br.x.toFixed(2)},${br.y.toFixed(2)}`}
            fill="rgba(0,0,0,0.4)" transform="translate(0.5,0.8)"
          />
        )}
        {/* Needle */}
        {value != null && (
          <path d={needlePath} fill="#e2e8f0" />
        )}
        {/* Pivot circle */}
        {value != null && (
          <>
            <circle cx={CX} cy={CY} r="3.2" fill="#1e2a3a" />
            <circle cx={CX} cy={CY} r="1.8" fill={color} />
          </>
        )}
      </svg>
      <div className="mg-value-row">
        <span className="mg-value" style={{ color: value != null ? color : '#7a8ba5', fontSize: value != null ? '12px' : '8.5px' }}>
          {value != null ? value : (motivo || 'sem dados')}
        </span>
        <span className="mg-sublabel">{value != null ? valueLabel : ''}</span>
      </div>
    </div>
  );
}

export default function MarketGauges() {
  const [fg,  setFg]  = useState(null);
  const [vix, setVix] = useState(null);
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [fgRes, vixRes, overviewRes] = await Promise.allSettled([
          fetch(`${BACKEND}/api/market/feargreed`).then(r => r.json()),
          fetch(`${BACKEND}/api/market/vix`).then(r => r.json()),
          fetch(`${BACKEND}/api/market/overview`).then(r => r.json()),
        ]);
        if (fgRes.status === 'fulfilled' && fgRes.value) setFg(fgRes.value);
        if (vixRes.status === 'fulfilled' && vixRes.value) setVix(vixRes.value);
        if (overviewRes.status === 'fulfilled' && overviewRes.value) setOverview(overviewRes.value);
      } catch (_) {}
    };
    fetchAll();
    const t = setInterval(fetchAll, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const fgVal  = fg?.value  ?? null;
  const vixVal = vix?.value ?? null;
  const closingDate = overview?.referenceDate
    ? new Date(`${overview.referenceDate}T12:00:00-03:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
    : null;
  const closingLabel = closingDate ? `fech. ${closingDate}` : 'sem data de fech.';
  const signedPct = value => value != null && Number.isFinite(Number(value))
    ? `${Number(value) > 0 ? '+' : ''}${Number(value).toFixed(1).replace('.', ',')}%`
    : '—';

  return (
    <div className="mg-root">
      <ArcGauge
        value={vixVal}
        max={50}
        color={vixVal != null ? vixColor(vixVal) : '#3d4860'}
        label="VIX"
        valueLabel={vixVal != null ? vixLabel(vixVal) : ''}
        motivo={vix?.motivo || 'sem dados de VIX'}
      />
      <div className="mg-divider" />
      <ArcGauge
        value={fgVal}
        max={100}
        color={fgVal != null ? fgColor(fgVal) : '#3d4860'}
        label="Medo/Ganância"
        valueLabel={fgVal != null ? fgLabel(fgVal) : ''}
        motivo={fg?.motivo || 'sem dados de Medo/Ganância'}
      />
      <div className="mg-divider" />
      <div className="mg-local" aria-label="Contexto local do mercado">
        <div className="mg-local-item">
          <span>IBOV vs MM200</span>
          <strong className={overview?.ibov?.vsMm200Pct != null ? (Number(overview.ibov.vsMm200Pct) >= 0 ? 'positive' : 'negative') : ''}>
            {overview?.ibov?.vsMm200Pct != null ? signedPct(overview.ibov.vsMm200Pct) : (overview?.ibov?.motivo || 'sem dados de IBOV')}
          </strong>
        </div>
        <div className="mg-local-item">
          <span>IBOV no dia</span>
          <strong className={overview?.ibov?.dailyChangePct != null ? (Number(overview.ibov.dailyChangePct) >= 0 ? 'positive' : 'negative') : ''}>
            {overview?.ibov?.dailyChangePct != null ? signedPct(overview.ibov.dailyChangePct) : (overview?.ibov?.motivo || 'sem dados de IBOV')}
          </strong>
        </div>
        <div className="mg-local-item">
          <span>Universo &gt; MM200</span>
          <strong>{overview?.universe?.aboveMm200Pct != null && Number.isFinite(Number(overview.universe.aboveMm200Pct)) ? `${Number(overview.universe.aboveMm200Pct).toFixed(0)}%` : (overview?.universe?.motivo || 'sem dados do universo')}</strong>
        </div>
        <small>{closingLabel}</small>
      </div>
    </div>
  );
}
